/**
 * Experiences Loading Placeholder Component
 * Displays skeleton loading placeholders matching the experience screen structure
 * Shows sections with horizontal scrolling lists (initial) or grid layout (search)
 */

import React from 'react';
import {
  spacing,
  borders,
  backgroundColors,
  strokeColors,
  typography,
} from '@/design-system/utils';

export interface ExperiencesLoadingPlaceholderProps {
  /**
   * Pattern variant for different loading states
   * 'initial' - Initial load pattern (sections with horizontal scroll)
   * 'search' - Search pattern (grid layout when filters applied)
   * @default 'initial'
   */
  pattern?: 'initial' | 'search';
  /**
   * Number of skeleton sections to display (for initial pattern)
   * @default 3
   */
  sectionCount?: number;
  /**
   * Number of skeleton cards per section (for initial pattern) or total cards (for search pattern)
   * @default 6
   */
  cardsPerSection?: number;
}

export const ExperiencesLoadingPlaceholder: React.FC<
  ExperiencesLoadingPlaceholderProps
> = ({ pattern = 'initial', sectionCount = 3, cardsPerSection = 6 }) => {
  // Generate skeleton cards for a single section
  const generateSkeletonCards = (count: number, isSearch = false) => {
    const cards = [];
    for (let i = 0; i < count; i++) {
      if (isSearch) {
        // Search pattern: varied widths and content
        const titleWidths = ['80%', '70%', '90%', '75%'];
        const hasTags = i % 2 === 0;

        cards.push({
          key: i,
          titleWidth: titleWidths[i % titleWidths.length],
          hasTags,
          priceWidth: i % 3 === 0 ? '60%' : '80%',
        });
      } else {
        // Initial pattern: uniform cards
        cards.push({
          key: i,
          titleWidth: '85%',
          hasTags: true,
          priceWidth: '70%',
        });
      }
    }
    return cards;
  };

  // Generate section titles for initial pattern
  const sectionTitles = ['Nexgo Journeys', 'Nexgo Spaces', 'Guided Activities'];
  const sectionDescriptions = [
    'Thoughtfully curated journeys for meaningful and refined exploration',
    'Unique stays handpicked for comfort and character',
    'Handpicked activities at every destination',
  ];

  // Single card skeleton component
  const SkeletonCard = ({
    card,
  }: {
    card: {
      key: number;
      titleWidth: string;
      hasTags: boolean;
      priceWidth: string;
    };
  }) => (
    <div
      style={{
        display: 'flex',
        backgroundColor: backgroundColors.primary,
        border: `1px solid ${strokeColors.light}`,
        borderRadius: borders.radius.xl,
        padding: spacing[2], // 8px
        flexDirection: 'column',
        gap: spacing[1.5], // 6px
        boxShadow: '0px 4px 12px 0px rgba(0,0,0,0.06)',
        boxSizing: 'border-box',
        flexShrink: 0,
        width: '220px',
        maxWidth: '220px',
        minWidth: '220px',
      }}
    >
      {/* Image Container Skeleton */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '160px',
          borderRadius: borders.radius.lg,
          overflow: 'hidden',
          backgroundColor: '#E5E7EB',
          backgroundImage: 'linear-gradient(135deg, #E5E7EB 0%, #D1D5DB 100%)',
        }}
      >
        {/* Badge Skeleton */}
        <div
          style={{
            position: 'absolute',
            top: spacing[1],
            left: spacing[1],
            zIndex: 2,
          }}
        >
          <div
            style={{
              backgroundColor: '#fed7aa',
              border: '1px solid #fdba74',
              borderRadius: borders.radius.lg,
              paddingLeft: spacing[1],
              paddingRight: spacing[2],
              paddingTop: spacing[1],
              paddingBottom: spacing[1],
              display: 'flex',
              gap: spacing[1],
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                backgroundColor: '#fdba74',
                borderRadius: borders.radius.md,
                padding: spacing[0.5],
                width: '12px',
                height: '12px',
              }}
              className='animate-pulse'
            />
            <div
              style={{
                height: '10px',
                width: '50px',
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                borderRadius: '2px',
              }}
              className='animate-pulse'
            />
          </div>
        </div>
      </div>

      {/* Content Container Skeleton */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[1.5],
          paddingLeft: spacing[1],
          paddingRight: spacing[1],
          paddingTop: spacing[1.5],
          paddingBottom: spacing[1.5],
        }}
      >
        {/* Tags Skeleton */}
        {card.hasTags && (
          <div
            style={{
              display: 'flex',
              gap: spacing[1],
              alignItems: 'center',
              justifyContent: 'flex-start',
              flexWrap: 'wrap',
            }}
          >
            {[1, 2, 3].map((tag, index) => (
              <React.Fragment key={tag}>
                <div
                  style={{
                    height: '14px',
                    width: index === 0 ? '60px' : index === 1 ? '50px' : '55px',
                    backgroundColor: backgroundColors.shade,
                    borderRadius: '2px',
                  }}
                  className='animate-pulse'
                />
                {index < 2 && (
                  <span
                    style={{
                      color: backgroundColors.shade,
                      fontSize: typography.size.xs,
                      display: 'inline-block',
                      width: '4px',
                      textAlign: 'center',
                    }}
                  >
                    •
                  </span>
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Title Skeleton */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing[1],
          }}
        >
          <div
            style={{
              height: '18px',
              width: card.titleWidth,
              backgroundColor: backgroundColors.shade,
              borderRadius: '4px',
              marginBottom: '4px',
            }}
            className='animate-pulse'
          />
          <div
            style={{
              height: '18px',
              width: '70%',
              backgroundColor: backgroundColors.shade,
              borderRadius: '4px',
            }}
            className='animate-pulse'
          />

          {/* Location Skeleton */}
          <div
            style={{
              display: 'flex',
              gap: spacing[1],
              alignItems: 'center',
              marginTop: spacing[1],
            }}
          >
            <div
              style={{
                width: '12px',
                height: '12px',
                backgroundColor: backgroundColors.shade,
                borderRadius: '2px',
                flexShrink: 0,
              }}
              className='animate-pulse'
            />
            <div
              style={{
                height: '14px',
                width: '100px',
                backgroundColor: backgroundColors.shade,
                borderRadius: '4px',
              }}
              className='animate-pulse'
            />
          </div>
        </div>

        {/* Description Skeleton */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing[1],
            minHeight: '33.6px',
          }}
        >
          <div
            style={{
              height: '12px',
              width: '100%',
              backgroundColor: backgroundColors.shade,
              borderRadius: '4px',
            }}
            className='animate-pulse'
          />
          <div
            style={{
              height: '12px',
              width: '85%',
              backgroundColor: backgroundColors.shade,
              borderRadius: '4px',
            }}
            className='animate-pulse'
          />
        </div>

        {/* Price Skeleton */}
        <div
          style={{
            backgroundColor: '#fff7ed',
            borderRadius: borders.radius.lg,
            padding: `${spacing[3]} 0`,
            display: 'flex',
            gap: spacing[1],
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <div
            style={{
              height: '20px',
              width: card.priceWidth,
              backgroundColor: backgroundColors.shade,
              borderRadius: '4px',
            }}
            className='animate-pulse'
          />
          <div
            style={{
              height: '14px',
              width: '60px',
              backgroundColor: backgroundColors.shade,
              borderRadius: '4px',
            }}
            className='animate-pulse'
          />
        </div>
      </div>
    </div>
  );

  // If search pattern, show grid layout
  if (pattern === 'search') {
    const searchCards = generateSkeletonCards(cardsPerSection, true);
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[4],
          width: '100%',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, 220px)',
            columnGap: spacing[4],
            rowGap: spacing[12],
            width: '100%',
            justifyContent: 'center',
          }}
        >
          {searchCards.map(card => (
            <SkeletonCard key={card.key} card={card} />
          ))}
        </div>

        <style jsx>{`
          @keyframes pulse {
            0%,
            100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
          .animate-pulse {
            animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          }
        `}</style>
      </div>
    );
  }

  // Initial pattern: show sections with headers and horizontal scroll
  const sections = Array.from({ length: sectionCount }, (_, i) => ({
    id: i,
    title: sectionTitles[i] || `Section ${i + 1}`,
    description: sectionDescriptions[i] || '',
    cards: generateSkeletonCards(cardsPerSection, false),
  }));

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[12], // 48px gap between sections (matches ExperienceSectionList)
        width: '100%',
      }}
    >
      {sections.map(section => (
        <div
          key={section.id}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing[3],
            width: '100%',
          }}
        >
          {/* Section Header Skeleton */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: spacing[1],
              alignItems: 'center',
              textAlign: 'center',
              width: '100%',
              paddingBottom: spacing[2],
            }}
          >
            {/* Title Skeleton */}
            <div
              style={{
                height: '32px',
                width: '300px',
                backgroundColor: backgroundColors.shade,
                borderRadius: '4px',
                maxWidth: '853px',
              }}
              className='animate-pulse'
            />
            {/* Description Skeleton */}
            <div
              style={{
                height: '20px',
                width: '500px',
                backgroundColor: backgroundColors.shade,
                borderRadius: '4px',
                maxWidth: '853px',
                marginTop: spacing[1],
              }}
              className='animate-pulse'
            />
          </div>

          {/* Horizontal Scrollable Cards Container */}
          <div
            style={{
              position: 'relative',
              width: '100%',
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: spacing[4], // 16px gap between cards
                overflowX: 'auto',
                overflowY: 'hidden',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                paddingBottom: spacing[2],
                width: '100%',
                boxSizing: 'border-box',
                WebkitOverflowScrolling: 'touch',
              }}
              className='scrollbar-hide'
            >
              {section.cards.map(card => (
                <SkeletonCard key={card.key} card={card} />
              ))}
            </div>

            {/* Scroll Right Button Skeleton */}
            <div
              style={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
              }}
            >
              <div
                style={{
                  width: '44px',
                  height: '44px',
                  backgroundColor: backgroundColors.primary,
                  border: `1px solid ${strokeColors.medium}`,
                  borderRadius: '50%',
                  boxShadow: '4px 4px 12px 0px rgba(0, 0, 0, 0.12)',
                }}
                className='animate-pulse'
              />
            </div>
          </div>
        </div>
      ))}

      {/* CSS animation for pulse effect */}
      <style jsx>{`
        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};
